/**
 * 店舗の詳細ページのルートコンポーネント
 * 
 * テンプレートはJinjaの以下と紐づく
 * templates/organizations/{...}/point.html > templates/default/point.html
 */
import { defineComponent, onMounted, ref } from 'vue'
import PointPage from "@/organizations/_default/point"
import Flyers from "./components/Flyers.vue"
import PointSearchConditions from "@/components/common/PointSearchConditions.vue"
import { Filters } from '@/services/Filters.ts'
import { useStore } from 'vuex'
import { WindowSize } from '@/constants'
import api from '@/api.ts'
import PointService from '@/services/PointService.ts'
import geohashlib from '@/core/geohash.js'

export default defineComponent({
  extends: PointPage,
  components: {Flyers,PointSearchConditions},
  setup(props, context) {
    const store = useStore()
    const page = PointPage.setup(props, context)

    const windowSize = ref(store.state.windowSize)
    const isPC = ref(windowSize.value === WindowSize.LARGE)


    const showMap = ref(false)

    const setShowMap = () => {
      showMap.value = !showMap.value
      storelocator.analytics.client?.send('ShowMapByUser', {
      })
    }

    /**
     * 検索条件の件数を取得
     * @returns {number} 検索条件の数
     */
    const getConditionsCount = () => {
      const uniqueNames = new Set()
      return Filters
        .getPointFilters(
          Filters.TYPE_SEARCH_CONDITIONS,
          page.point.value,
          Filters.getFilterTree(Filters.TYPE_SEARCH_CONDITIONS)
        )
        // 表示されている検索条件のみを取得
        .filter(condition => condition.is_show)
        // 親が表示されている場合のみ取得
        .filter(condition => {
          const parent = Filters.findParentById(Filters.TYPE_SEARCH_CONDITIONS, condition.id)
          if (!parent?.is_show) {
            return false
          }     
          return true
        })
        .reduce((count, condition) => {
          if (!uniqueNames.has(condition.name)) {
            uniqueNames.add(condition.name)
            return count + 1
          }
          return count
        }, 0)
    }

    /**
     * 店舗の持つ有効な検索条件の件数
     * @type {number}
     */
    const conditionsCount = getConditionsCount()

    onMounted(() => {

      store.dispatch('onWindowResized')

      // 初期表示時の値で固定（一度だけ設定）
      windowSize.value = store.state.windowSize
      isPC.value = windowSize.value === WindowSize.LARGE
      if(!isPC.value) {
        const pointMap = document.getElementById('PointMap')
        pointMap.style.display = 'none'
      }

      showMap.value = isPC.value ? true : false

      // SPで地図を表示しない状態でも「近くの店舗」を取得して表示する
      if (!isPC.value && !showMap.value) {
        void fetchNearPointsWithoutMap().catch((e) => {
          console.warn('[beisia] failed to fetch near points without map', e)
        })
      }

    })

    // 近隣探索で使う geohash の桁数。PC（地図あり）側の設定に合わせる（無ければ3）
    const NEARBY_GEOHASH_DIGITS = storelocator.map?.points?.geohash?.digits || 3
    // 画面に表示する「近くの店舗」の最大件数
    const MAX_NEAR_POINTS = 6

    /**
     * 度(deg)をラジアン(rad)に変換する
     * @param {number} deg 度（degree）
     * @returns {number} ラジアン（radian）
     */
    function toRad(deg) {
      return (deg * Math.PI) / 180
    }

    /**
     * 2点間の直線距離をメートルで返す（haversine）
     * @param {number} lat1 緯度（度）
     * @param {number} lon1 経度（度）
     * @param {number} lat2 緯度（度）
     * @param {number} lon2 経度（度）
     * @returns {number} 距離（メートル）
     */
    function distanceMeters(lat1, lon1, lat2, lon2) {
      const R = 6371000
      const dLat = toRad(lat2 - lat1)
      const dLon = toRad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    }

    /**
     * 数値を指定範囲[min, max]に収める（範囲外なら端の値に丸める）
     *
     * @param {number} v 対象の値
     * @param {number} min 最小値
     * @param {number} max 最大値
     * @returns {number} 範囲内に収めた値
     */
    function clamp(v, min, max) {
      return Math.max(min, Math.min(max, v))
    }


    /**
     * 店舗から近隣店舗までの最短距離（メートル）を返す
     *
     * @param {number} lat 緯度（度）
     * @param {number} lon 経度（度）
     * @param {number[]} bboxArray bbox配列（[minLat, minLon, maxLat, maxLon] / 度）
     * @returns {number} 最短距離（メートル）
     */
    function minDistanceToBboxMeters(lat, lon, bboxArray) {
      const clampedLat = clamp(lat, bboxArray[0], bboxArray[2])
      const clampedLon = clamp(lon, bboxArray[1], bboxArray[3])
      return distanceMeters(lat, lon, clampedLat, clampedLon)
    }

    /**
     * 近隣店舗の距離制限（km）を取得する
     * @returns {number|null} 最大距離（km）/ 距離制限なしの場合はnull
     */
    function getNearbyMaxDistanceKm() {
      if (storelocator.point?.nearby?.max?.enabled && storelocator.point?.nearby?.max?.distance > 0) {
        return storelocator.point.nearby.max.distance
      }
      // 距離制限なし
      return null
    }

    /**
     * 選択した店舗のbboxを作る
     * @param {number} lat 緯度（度）
     * @param {number} lon 経度（度）
     * @param {number} radiusKm 半径（km）
     * @returns {{minLat:number,maxLat:number,minLon:number,maxLon:number}} bbox（度）
     */
    function buildBboxByRadiusKm(lat, lon, radiusKm) {
      const latDelta = radiusKm / 111.32
      const cos = Math.cos(toRad(lat))
      const lonDelta = radiusKm / (111.32 * Math.max(0.000001, cos))
      return {
        minLat: Math.max(-90, lat - latDelta),
        maxLat: Math.min(90, lat + latDelta),
        minLon: Math.max(-180, lon - lonDelta),
        maxLon: Math.min(180, lon + lonDelta),
      }
    }

    async function fetchNearPointsWithoutMap() {
      if (!storelocator.point?.nearby?.enabled) {
        return
      }

      const basePoint = page.point.value
      const latitude = basePoint?.latitude
      const longitude = basePoint?.longitude
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return
      }

      const unique = new Map()


      const maxDistanceKm = getNearbyMaxDistanceKm()
      const shouldApplyMaxDistance = maxDistanceKm !== null

      const toSortedNearPoints = () => {
        return Array.from(unique.values())
          .filter(p => p && p.id !== basePoint.id && p.key !== basePoint.key)
          .map(p => {
            p.distance = distanceMeters(
              basePoint.latitude,
              basePoint.longitude,
              p.latitude,
              p.longitude
            )
            return p
          })
          .filter(p => {
            if (shouldApplyMaxDistance) {
              return (p.distance / 1000) <= maxDistanceKm
            }
            return true
          })
          .sort((a, b) => a.distance - b.distance)
          .slice(0, MAX_NEAR_POINTS)
      }

      // - 距離制限あり: maxDistanceKm
      // - 距離制限なし: PCのbounds相当の広さになるよう 0.3km
      const radiusKm = shouldApplyMaxDistance ? maxDistanceKm : 0.3

      // 中心（basePoint）＋半径（radiusKm）から探索範囲のbbox（緯度経度の矩形）を作る
      const bbox = buildBboxByRadiusKm(basePoint.latitude, basePoint.longitude, radiusKm)
      // bboxをカバーする geohash の一覧を作る
      let geohashes = geohashlib.bboxes(
        bbox.minLat, bbox.minLon,
        bbox.maxLat, bbox.maxLon,
        NEARBY_GEOHASH_DIGITS
      )

      geohashes = [...new Set(geohashes)]
        .map((hash) => {
          const b = geohashlib.decode_bbox(hash)
          const d = minDistanceToBboxMeters(basePoint.latitude, basePoint.longitude, b)
          return { hash, d }
        })
        // 中心点に近いセルから順に並べる（近いエリアから優先的に取得する）
        .sort((a, b) => a.d - b.d)
        .map(x => x.hash)

      // geohash数の上限を設定（過剰なリクエストを防ぐ）
      const MAX_GEOHASHES = 20
      geohashes = geohashes.slice(0, MAX_GEOHASHES)

      // 同時実行数を制限しつつ、距離順に取得する
      const CONCURRENCY_LIMIT = 3
      // 早期終了チェックを行う前に必要な最小ポイント数（バッファとして2倍を設定）
      const EARLY_TERMINATION_BUFFER = MAX_NEAR_POINTS * 2

      for (let i = 0; i < geohashes.length; i += CONCURRENCY_LIMIT) {
        const uniqueSizeBeforeBatch = unique.size

        // CONCURRENCY_LIMIT件ずつバッチで取得
        const batch = geohashes.slice(i, i + CONCURRENCY_LIMIT)
        const responses = await Promise.all(
          batch.map((hash) => api.fetchPointsByGeohash(hash))
        )

        for (const res of responses) {
          if (!res || res.status === 404) {
            continue
          }
          if (res.status !== 200) {
            continue
          }

          for (const p of (res.data?.items || [])) {
            const parsed = PointService.parsePoint(p)
            if (!unique.has(parsed.id)) {
              unique.set(parsed.id, parsed)
            }
          }
        }

        // 必要な件数が既に揃っていれば打ち切る
        // 新しいポイントが追加され、かつ十分な数がある場合のみチェック
        if (unique.size > uniqueSizeBeforeBatch && unique.size >= EARLY_TERMINATION_BUFFER) {
          const currentCount = toSortedNearPoints().length
          if (currentCount >= MAX_NEAR_POINTS) {
            break
          }
        }
      }

      page.nearPoints.value = toSortedNearPoints()

    }

    return {
      ...page,
      conditionsCount,
      showMap,
      isPC,
      setShowMap
    }
  }
})